import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';

@Component({
  selector: 'patient-custom-graph',
  standalone: true,
  imports: [CommonModule, ChartModule],
  templateUrl: './custom-graph.component.html',
  styleUrl: './custom-graph.component.css'
})
export class CustomGraphComponent implements OnChanges {

  @Input() labels: string[] = [];
  @Input() dataValues: number[] = [];
  @Input() lineColor: string = '#F5A623'; // Default orange
  @Input() tooltipSuffix: string = '';
  @Input() yAxisMin?: number;
  @Input() yAxisMax?: number;

  // For target line features (like BMI Healthy Range)
  @Input() targetLineValue?: number;
  @Input() targetLineLabel: string = 'Healthy Range';

  data: any;
  options: any;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['labels'] || changes['dataValues'] || changes['targetLineValue']) {
      this.initChart();
    }
  }

  initChart() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color') || '#4b5563';
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#9ca3af';
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#e5e7eb';

    const datasets: any[] = [
      {
        label: this.tooltipSuffix,
        data: this.dataValues,
        fill: true,
        borderColor: this.lineColor,
        tension: 0.4,
        backgroundColor: this.getComputedBackground(this.lineColor),
        pointBackgroundColor: this.lineColor,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      }
    ];

    // If a target line is requested, add it as a secondary dashed dataset
    if (this.targetLineValue !== undefined && this.labels.length > 0) {
      datasets.push({
        label: this.targetLineLabel,
        data: Array(this.labels.length).fill(this.targetLineValue),
        fill: false,
        borderColor: '#10B981', // green dashed line
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
      });
    }

    this.data = {
      labels: this.labels,
      datasets: datasets
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // We will handle our own legends outside the graph if needed
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#ffffff',
          titleColor: '#111827',
          bodyColor: '#4b5563',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          callbacks: {
            label: (context: any) => {
              if (context.dataset.label === this.targetLineLabel) {
                   return `Target: ${context.parsed.y}`;
              }
              return `${context.parsed.y} ${this.tooltipSuffix}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: { size: 11 }
          },
          grid: {
            display: false,
          }
        },
        y: {
          min: this.yAxisMin,
          max: this.yAxisMax,
          ticks: {
            color: textColorSecondary,
            font: { size: 11 },
            padding: 10
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
            borderDash: [4, 4]
          }
        }
      }
    };
  }

  // Fallback flat transparent color representation simulating gradient inside PrimeNG wrapper
  private getComputedBackground(hexCode: string): string {
    // Basic hex to rgba converter to enable semi-transparent fills
    if (hexCode.startsWith('#') && hexCode.length === 7) {
        const r = parseInt(hexCode.slice(1, 3), 16);
        const g = parseInt(hexCode.slice(3, 5), 16);
        const b = parseInt(hexCode.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, 0.15)`;
    }
    return 'rgba(245, 166, 35, 0.15)'; // fallback light orange
  }
}
